import type { ReactNode } from "react";

import type { TFunction } from "@/i18n/catalog";

import { ExamineWaste } from "@/components/dashboard/examine-waste";
import { L1BreakdownWidget } from "@/components/dashboard/l1-breakdown-widget";
import { WhatsWorking } from "@/components/dashboard/whats-working";
import { WhenHeatmap, type HeatmapDrill } from "@/components/dashboard/when-heatmap";
import { WhereMoneyGoes } from "@/components/dashboard/where-money-goes";
import { BreakdownDonut } from "@/components/waste/reports/breakdown-donut";
import { CategoryBar } from "@/components/waste/reports/category-bar";
import { TierDistributionBar } from "@/components/waste/reports/tier-distribution-bar";

import type { Lens } from "@/hooks/use-lens";
import type { WasteLineStats } from "@/hooks/use-line-stats";
import type { WasteStats } from "@/hooks/use-waste-stats";
import type { WidgetId } from "@/lib/dashboard-widgets";
import type { Split } from "@/hooks/use-explore-state";
import {
  exploreHref,
  MEASURE_FOR_LENS,
  type LinkFilters,
  type LinkWindow,
} from "@/lib/explore-link";
import { wasteTypeLabel, WASTE_TYPE_CHART_COLOR } from "@/lib/waste-types";

/** Literal keys in a lookup table, never `t(`reference.serviceTime.${key}`)` — a key built at
 * runtime is invisible to `check-i18n.mjs` and would be reported as dead (POS2-1134). */
const SERVICE_TIME_KEYS: Record<string, string> = {
  breakfast: "reference.serviceTime.breakfast",
  lunch: "reference.serviceTime.lunch",
  dinner: "reference.serviceTime.dinner",
  unassigned: "reference.serviceTime.unassigned",
};

const SERVICE_TIME_COLORS: Record<string, string> = {
  breakfast: "#f59e0b",
  lunch: "#3b82f6",
  dinner: "#6366f1",
  unassigned: "#9ca3af",
};

/** Everything the tail widgets read — the dashboard page assembles this once. */
export type WidgetContext = {
  /** The widgets render from a module-level registry, so `t` arrives with the context rather
   * than from a hook — a module-level `t()` would capture the locale at import (POS2-1134). */
  t: TFunction;
  stats: WasteStats;
  lens: Lens;
  currency: string;
  /** First line-stats load (feeds working/examine skeletons). */
  lineStatsLoading: boolean;
  lines: WasteLineStats[] | undefined;
  periodLengthDays: number;
  /** Narrows The Proof feed to a waste line (evidence links). */
  onViewLineEvidence: (line: { lineKey: string; label: string }) => void;
  /** Narrows The Proof feed to a heatmap hot cell. */
  onDrillHeatCell: (cell: HeatmapDrill) => void;
  /** The dashboard's window as site-local civil days, for the "Explore this" links
   * (POS2-886). Null for an all-time range — there is no concrete window to hand over,
   * so the affordance is not offered. */
  exploreWindow: LinkWindow | null;
  /** The active scope carried into Explore alongside the window. */
  exploreFilters: LinkFilters;
};

type WidgetEntry = {
  id: WidgetId;
  /** A catalogue key, resolved by whoever renders the frame — see the note on `WidgetContext.t`. */
  titleKey: string;
  render: (ctx: WidgetContext) => ReactNode;
};

/** A widget's "Explore this" target: the dashboard's window and scope, split by the dimension
 * the widget is already showing.
 *
 * Only splits Explore actually offers may be used. `dish` is still a valid value in the type,
 * the URL parser and the engine, but Mark had it taken out of the Split control (POS2-851) — so
 * a link setting it would open a state the popover cannot show or return to. That is why
 * Examine waste, a by-dish view, carries no link at all: with no dish dimension to hand over it
 * would just open Explore's default chart, which is not "explore THIS". A test keeps the
 * builders and the Split control in step. */
function splitLink(ctx: WidgetContext, split: Split): string | null {
  if (!ctx.exploreWindow) return null;
  return exploreHref({
    gran: "daily",
    window: ctx.exploreWindow,
    split,
    measure: MEASURE_FOR_LENS[ctx.lens],
    filters: ctx.exploreFilters,
  });
}

/**
 * The widget registry (POS2-877): new widgets ship by adding an entry here and
 * an id in lib/dashboard-widgets.ts — layout logic never changes. The legacy
 * Phase 5 widgets stay available as library widgets (default off).
 */
export const WIDGET_REGISTRY: Record<WidgetId, WidgetEntry> = {
  money: {
    id: "money",
    titleKey: "dashboard.widget.money",
    render: (ctx) => (
      <WhereMoneyGoes
        buckets={ctx.stats.by_waste_type}
        lens={ctx.lens}
        currency={ctx.currency}
        exploreHref={splitLink(ctx, "reason")}
      />
    ),
  },
  when: {
    id: "when",
    titleKey: "dashboard.widget.when",
    render: (ctx) => (
      <WhenHeatmap
        buckets={ctx.stats.by_service_dow}
        lens={ctx.lens}
        currency={ctx.currency}
        onDrill={ctx.onDrillHeatCell}
        exploreHref={splitLink(ctx, "service")}
      />
    ),
  },
  working: {
    id: "working",
    titleKey: "dashboard.widget.working",
    render: (ctx) => (
      <WhatsWorking
        isLoading={ctx.lineStatsLoading}
        lines={ctx.lines}
        lens={ctx.lens}
        currency={ctx.currency}
        periodLengthDays={ctx.periodLengthDays}
        onViewEvidence={ctx.onViewLineEvidence}
      />
    ),
  },
  examine: {
    id: "examine",
    titleKey: "dashboard.widget.examine",
    render: (ctx) => (
      <ExamineWaste
        isLoading={ctx.lineStatsLoading}
        lines={ctx.lines}
        lens={ctx.lens}
        currency={ctx.currency}
        onViewEvidence={ctx.onViewLineEvidence}
      />
    ),
  },
  "waste-type-donut": {
    id: "waste-type-donut",
    titleKey: "dashboard.widget.wasteTypeDonut",
    render: (ctx) => (
      <BreakdownDonut
        title={ctx.t("dashboard.widget.wasteTypeDonut")}
        description={ctx.t("dashboard.wasteTypeDonutDescription")}
        buckets={ctx.stats.by_waste_type}
        labelOf={(key) => wasteTypeLabel(ctx.t, key)}
        colorOf={(key) => WASTE_TYPE_CHART_COLOR[key]}
      />
    ),
  },
  "service-donut": {
    id: "service-donut",
    titleKey: "dashboard.widget.serviceDonut",
    render: (ctx) => (
      <BreakdownDonut
        title={ctx.t("dashboard.widget.serviceDonut")}
        description={ctx.t("dashboard.serviceDonutDescription")}
        buckets={ctx.stats.by_service_time}
        labelOf={(key) => (SERVICE_TIME_KEYS[key] ? ctx.t(SERVICE_TIME_KEYS[key]) : key)}
        colorOf={(key) => SERVICE_TIME_COLORS[key]}
      />
    ),
  },
  "tier-bar": {
    id: "tier-bar",
    titleKey: "dashboard.widget.tierBar",
    render: (ctx) => <TierDistributionBar buckets={ctx.stats.by_classification_level} />,
  },
  "category-bar": {
    id: "category-bar",
    // Must match the component's ChartCard title — the add-chip and remove
    // toast name the widget the user will see.
    titleKey: "dashboard.widget.categoryBar",
    render: (ctx) => <CategoryBar buckets={ctx.stats.by_category} currency={ctx.currency} />,
  },
  "l1-breakdown": {
    id: "l1-breakdown",
    titleKey: "dashboard.widget.l1Breakdown",
    render: (ctx) => <L1BreakdownWidget buckets={ctx.stats.by_l1_reason} />,
  },
};
